import React from 'react';
import LegalLayout from '../components/LegalLayout';

const LicenseTerms = () => (
  <LegalLayout title="License Terms" lastUpdated="August 24, 2026">
    <p>
      These License Terms govern your rights to use, customize and commercially distribute products and content created using the Lumera platform (<strong>createbylumera.com</strong>), operated by Create by Lumera.
    </p>
    <p>
      By purchasing a Lumera product, subscribing to Lumera Pro, or using the Lumera platform, you agree to be bound by these License Terms in addition to our <a href="/terms">Terms of Service</a>.
    </p>

    <h2>1. License Tiers</h2>

    <h3>1.1 Personal Use License (Free Plan)</h3>
    <p>
      Users on the free plan are granted a <strong>Personal Use License</strong>. You may create and customize products for personal, non-commercial use. You may export products where permitted by the free plan. You may <strong>not</strong> sell, commercially distribute, or sublicense products created under the Personal Use License.
    </p>

    <h3>1.2 Commercial License (Individual Purchase)</h3>
    <p>
      Every individually purchased Lumera product automatically includes a <strong>Commercial License</strong> for that specific product and your finished creations made from it. The license is granted upon confirmed payment and remains valid indefinitely, subject to these terms.
    </p>

    <h3>1.3 Commercial License (Lumera Pro)</h3>
    <p>
      Lumera Pro subscribers (monthly or yearly) are granted a Commercial License for each eligible product they create and export while their Pro subscription is active. If you create and export a product while your Pro subscription is active, your Commercial License for that finished creation remains valid permanently, even if you later cancel your Pro subscription. Cancellation prevents you from creating or exporting new Pro products, but does <strong>not</strong> revoke commercial rights to products you legitimately created while licensed.
    </p>

    <h2>2. What the Commercial License Permits</h2>
    <p>The Commercial License grants you a <strong>non-exclusive</strong> right to:</p>
    <ul>
      <li>Customize Lumera templates and generated products, including text, colors, typography, images and layouts.</li>
      <li>Add your own branding, logos and identity.</li>
      <li>Export finished products from the Lumera platform.</li>
      <li>Sell your finished digital products commercially, with <strong>unlimited end-product sales</strong>.</li>
      <li>Sell through Etsy, Shopify, your own website and other supported marketplaces.</li>
      <li>Sell the same finished end product to multiple customers.</li>
      <li>Use finished creations within your own business and marketing.</li>
      <li>Make reasonable modifications necessary to create your own finished commercial product.</li>
    </ul>

    <h2>3. What the Commercial License Does NOT Permit</h2>
    <p>You may <strong>not</strong>:</p>
    <ul>
      <li>Resell, redistribute or sublicense original Lumera templates as templates.</li>
      <li>Redistribute original Lumera source files or downloadable assets.</li>
      <li>Share your Lumera account or transfer access to Lumera.</li>
      <li>Repackage Lumera's template library as your own template library or competing product.</li>
      <li>Copy, reverse-engineer or redistribute the Lumera platform, generator or software.</li>
      <li>Extract Lumera assets for standalone resale.</li>
      <li>Sell substantially unmodified Lumera source templates as competing templates.</li>
      <li>Claim ownership of Lumera's platform, original templates, underlying design systems, software or proprietary assets.</li>
      <li>Trademark or claim exclusive rights over a Lumera base template or design that may also be available to other customers.</li>
    </ul>

    <h2>4. Ownership</h2>
    <p>
      This license grants you a non-exclusive commercial right to use, customize and sell eligible end products created using Lumera. Ownership of the Lumera platform, original templates, software, design systems and underlying proprietary assets remains with Create by Lumera. The Commercial License is non-exclusive.
    </p>

    <h2>5. Third-Party Assets</h2>
    <p>
      Lumera products may incorporate fonts, stock imagery, graphics, AI-generated content or other third-party materials. The Commercial License applies only to rights that Lumera is legally able to grant. Any third-party assets remain subject to their applicable third-party license terms. Lumera does not grant you broader rights than Lumera itself holds for such materials.
    </p>

    <h2>6. License Certificate</h2>
    <p>
      When you purchase an individual product or create and export an eligible commercial product while Pro is active, Lumera generates a downloadable Commercial License Certificate. This certificate confirms your specific license and references these License Terms. Each certificate includes a unique License ID that is stored in your account and can be verified by Lumera.
    </p>

    <h2>7. Termination</h2>
    <p>
      Lumera may revoke a Commercial License if you materially breach these License Terms. In such cases, you must cease commercial distribution of affected products. Licenses that are validly issued are not affected by subsequent cancellation of a Pro subscription, as described in section 1.3 above.
    </p>

    <h2>8. Updates to These Terms</h2>
    <p>
      We may update these License Terms from time to time. The version in effect at the time your license was issued applies to that specific license. We will communicate material changes through the platform.
    </p>

    <h2>9. Contact</h2>
    <p>
      For licensing questions, please contact us at <a href="mailto:hello@createbylumera.com">hello@createbylumera.com</a>.
    </p>
  </LegalLayout>
);

export default LicenseTerms;
